// mbscan manager-client へ同じ形で移植する予定（移植までは mbfab のみ）。移植後は片側だけで変えない
// （mbfab 固有の import を持たせない。zod 等の検証は呼び出し側が parse で渡す）

import type { Dispatch, SetStateAction } from 'react'
import { use, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'

import { FilterBarUrlContext } from './filter-bar-url-context'
import type { FilterBarUrlStateOptions, JsonObject } from './filter-bar-url-state'
import { canonicalizeFilterBarValue, decodeFilterBarState, encodeFilterBarState } from './filter-bar-url-state'

export type { FilterBarUrlStateOptions, JsonObject, JsonValue } from './filter-bar-url-state'

// 書き込み待ちの記録の上限。URL が変わらないまま積み上がり続けないようにする
const maxPending = 20

type Store<T> = {
  value: T
  // 最後に反映した URL の条件（canonical 文字列）。これと現在の URL が違えば URL が変わっている
  urlSeen: string
  // 自分が書き込んだ（書き込み予定の）URL の条件。連続入力で古い書き込みの結果が届いても、
  // 外部からの URL 変更と取り違えて状態を巻き戻さないために覚える
  pending: string[]
}

/**
 * 一覧の絞り込み状態。`syncUrl: true` で URL のクエリに同期し、詳細からの戻り・リロード・URL 共有で条件を保つ。
 * 初期値を useState の初期化関数で同期的に URL から作るのは、初回描画から条件つきで一覧を取得し、
 * 条件なしの取得 → 取り直しのちらつき・二重取得を起こさないため。
 * 書き込みは replace にする。絞り込みのたびに履歴を積むと、ブラウザの戻るが前の画面へ戻らなくなる
 */
export function useFilterBarState<T extends JsonObject>(
  defaults: T,
  options: FilterBarUrlStateOptions<T> = {},
): [T, Dispatch<SetStateAction<T>>] {
  const { parse } = options
  const requestedSyncUrl = options.syncUrl === true
  // syncUrl なしのときは URL を読み書きしないので、キーは使われない
  const urlKey = options.syncUrl === true ? options.urlKey : ''
  // URL への口が供給されていない場所（story・単体描画）では useState と同じ挙動にする
  const urlStore = use(FilterBarUrlContext)
  const activeStore = requestedSyncUrl ? urlStore : null
  const syncUrl = activeStore !== null
  useEffect(() => {
    if (activeStore === null) {
      return undefined
    }
    return activeStore.claimKey(urlKey)
  }, [activeStore, urlKey])
  const readUrlValue = (): unknown => activeStore?.read(urlKey)

  const subscribe = useCallback(
    (onChange: () => void) => activeStore?.subscribe(onChange) ?? (() => undefined),
    [activeStore],
  )
  // 値そのものでなく文字列にして返す。外部ストアのスナップショットは毎回同じ参照・値でなければならないため
  const urlCanon = useSyncExternalStore(subscribe, () => canonicalizeFilterBarValue(readUrlValue()))

  // この画面を開いた時点のパス。別の画面へ遷移している間（アンマウント前の遷移中）は URL を追わない。
  // 追うと、遷移先の URL に条件が無いことを「条件が外されたとき」と取り違えて既定値に戻し、条件なしの一覧取得が走る
  const [mountedPathname] = useState(() => activeStore?.pathname())
  const away = activeStore !== null && activeStore.pathname() !== mountedPathname

  const [store, setStore] = useState<Store<T>>(() => ({
    value: syncUrl ? decodeFilterBarState(defaults, readUrlValue(), parse) : defaults,
    urlSeen: urlCanon,
    pending: [],
  }))

  // ブラウザの戻る/進むなど外部からの URL 変更に状態を追従させる。
  // 描画中に state を調整するのは、effect で setState すると古い条件で 1 回描画・取得してしまうため
  const synced = ((): Store<T> => {
    if (!syncUrl || away || urlCanon === store.urlSeen) {
      return store
    }
    const index = store.pending.indexOf(urlCanon)
    if (index >= 0) {
      // 自分の書き込みが届いた。それより古い書き込みの結果はもう届かない（または上書きされた）ので一緒に捨てる
      return { ...store, urlSeen: urlCanon, pending: store.pending.slice(index + 1) }
    }
    return { value: decodeFilterBarState(defaults, readUrlValue(), parse), urlSeen: urlCanon, pending: [] }
  })()
  if (synced !== store) {
    setStore(synced)
  }

  const state = synced.value
  const stateCanon = canonicalizeFilterBarValue(encodeFilterBarState(state, defaults))
  const handled = useRef(stateCanon)
  useEffect(() => {
    if (activeStore === null || away || stateCanon === handled.current) {
      return
    }
    handled.current = stateCanon
    // 描画時点の urlCanon は古いことがある（連続入力で書き込みが先行している）ため、書き込む直前に実際の URL を読み直す
    if (stateCanon === canonicalizeFilterBarValue(activeStore.read(urlKey))) {
      return
    }
    activeStore.write(urlKey, encodeFilterBarState(state, defaults))
  })

  // setState の identity を保つため、最新の defaults は ref 経由で読む（呼び出し側が毎回新しいオブジェクトを渡しても内容は同じ）
  const latestDefaults = useRef(defaults)
  useEffect(() => {
    latestDefaults.current = defaults
  })
  const setState = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      setStore((prev) => {
        const next = typeof action === 'function' ? action(prev.value) : action
        const nextCanon = canonicalizeFilterBarValue(encodeFilterBarState(next, latestDefaults.current))
        const prevCanon = canonicalizeFilterBarValue(encodeFilterBarState(prev.value, latestDefaults.current))
        return {
          ...prev,
          value: next,
          pending: syncUrl && nextCanon !== prevCanon ? [...prev.pending, nextCanon].slice(-maxPending) : prev.pending,
        }
      })
    },
    [syncUrl],
  )

  return [state, setState]
}
